/**
 * ScaffoldWizardPage — `/scaffold` (queue #642 · docs/biz-specs/project-scaffold.md §6).
 * 3단계 마법사: ①프로젝트 메타 ②모듈 선택 ③미리보기·생성.
 *
 * S1(Take-this "스캐폴드에 담기" 진입점 딥링크): `?preselect=Cxxx` 쿼리 파라미터가 있으면
 * 해당 코드를 미리 선택한 채 ②단계에서 마법사를 연다(router state 대신 URL 쿼리 — 공유 가능
 * 딥링크로 채택).
 *
 * queue #682 §UAT-2026-07-14-R682 §R682-5: 담기 카트(`useScaffoldCart` · localStorage
 * `eraf.scaffold.cart`)가 도입되면서 ③④가 추가된다 — ③ 진입 시 `selectedCodes` 초기값을
 * 카트로 시드(`?preselect=` 딥링크는 카트에 merge 후 반영해 단일 경로로 수렴) · ④ ②단계
 * 체크 해제/추가를 카트와 양방향 동기화(`handleSelectedChange` 래핑 — `ScaffoldModuleStep`
 * 자체는 무변경, 부모가 `onChangeSelected` 콜백만 가로챈다).
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@ui-kit';
import { ScaffoldStepper } from '../components/ScaffoldStepper';
import { ScaffoldMetaStep } from '../components/ScaffoldMetaStep';
import { ScaffoldModuleStep } from '../components/ScaffoldModuleStep';
import { ScaffoldPreviewStep } from '../components/ScaffoldPreviewStep';
import { useScaffoldCart } from '../../_shared/hooks/useScaffoldCart';
import type { ScaffoldMetaFormValues } from '../schema/scaffold';

type WizardStep = 1 | 2 | 3;

export function ScaffoldWizardPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const cart = useScaffoldCart();

  const [step, setStep] = useState<WizardStep>(1);
  const [meta, setMeta] = useState<ScaffoldMetaFormValues | null>(null);
  // §R685-4: ②→미리보기 시도인데 meta 미입력이면 조용한 setStep(1) 복귀만으로는 사용자가
  // 왜 되돌아갔는지 알기 어렵다 — ①단계에 안내 배너를 세워 원인을 드러낸다. 메타 제출 성공
  // (ScaffoldMetaStep 의 onNext) 시 소거.
  const [metaRequired, setMetaRequired] = useState(false);
  // §R682-5③: 초기값을 카트로 시드 — 카트에 이미 담긴 모듈들이 마법사 진입과 동시에 체크된다.
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(() => new Set(cart.codes));

  // §S1 + §R682-5③: Take-this [스캐폴드에 담기] 진입점 — ?preselect=Cxxx 딥링크는 카트에
  // merge 후(cart.add) ②단계에서 마법사를 연다(딥링크·카트 단일 경로 수렴 · 하위호환 유지).
  useEffect(() => {
    const preselect = searchParams.get('preselect');
    if (preselect) {
      const code = preselect.toUpperCase();
      cart.add(code);
      setSelectedCodes((prev) => new Set(prev).add(code));
      setStep(2);
    }
    // 최초 마운트 시 1회만 반영 — 이후 검색 파라미터 변경은 무시(뒤로가기 시 재점프 방지).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // §R682-5④: ②단계 체크박스 = 카트의 편집 뷰(양방향) — 빠진 코드는 cart.remove, 새로 추가된
  // 코드는 cart.add. §R675 프리셋 "교체" 클릭(applyPreset)도 이 경로를 타 카트에 반영된다.
  function handleSelectedChange(next: Set<string>) {
    selectedCodes.forEach((code) => {
      if (!next.has(code)) cart.remove(code);
    });
    next.forEach((code) => {
      if (!selectedCodes.has(code)) cart.add(code);
    });
    setSelectedCodes(next);
  }

  return (
    <div>
      <PageHeader
        title={`📦 ${t('projectScaffold.title')}`}
        subtitle={t('projectScaffold.subtitle')}
        breadcrumb={
          <span>
            {t('common.nav.home')} / {t('projectScaffold.title')}
          </span>
        }
      />
      <div style={{ padding: 'var(--spacing-6)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <ScaffoldStepper current={step} />

          {step === 1 && (
            <ScaffoldMetaStep
              defaultValues={meta ?? undefined}
              metaRequired={metaRequired}
              onNext={(values) => {
                setMeta(values);
                setMetaRequired(false);
                setStep(2);
              }}
            />
          )}

          {step === 2 && (
            <ScaffoldModuleStep
              selectedCodes={selectedCodes}
              onChangeSelected={handleSelectedChange}
              // §S1 딥링크로 ②단계에 바로 진입한 경우 meta 가 아직 없다 — ③(미리보기)은
              // 프로젝트 메타가 필수이므로 ①단계로 돌려보내 먼저 채우게 한다(§R685-4: 이때
              // metaRequired 를 세워 ①단계에 안내 배너를 노출).
              onNext={() => {
                if (meta) {
                  setStep(3);
                } else {
                  setMetaRequired(true);
                  setStep(1);
                }
              }}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && meta && (
            <ScaffoldPreviewStep
              meta={meta}
              moduleCodes={[...selectedCodes].sort()}
              onBack={() => setStep(2)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
